import { Check, Copy01 } from '@untitledui/icons';

import type { CreateTeamApiKeyResponse } from '@/lib/api/teamApiKeys';
import { Button } from '@/lib/base/buttons/button';

interface ConfigureKondoCardProps {
  webhookUrl: string;
  webhookCopied: boolean;
  onCopyWebhook: () => void;
  newApiKey: CreateTeamApiKeyResponse | null;
  newKeyCopied: boolean;
  onCopyNewKey: () => void;
  isConnected: boolean;
}

export function ConfigureKondoCard({
  webhookUrl,
  webhookCopied,
  onCopyWebhook,
  newApiKey,
  newKeyCopied,
  onCopyNewKey,
  isConnected,
}: ConfigureKondoCardProps) {
  return (
    <div className='bg-bg-secondary rounded-lg border border-border-secondary p-4 space-y-4'>
      <div>
        <h3 className='text-sm font-semibold text-secondary'>
          Configure Kondo
        </h3>
        <p className='text-sm text-tertiary mt-1'>
          In Kondo's Custom Webhook form, fill in both the Destination and API
          Key fields below. Kondo marks the API Key as optional, but Ahoy
          requires it – webhooks sent without it will be rejected.
        </p>
      </div>

      {/* Destination */}
      <div>
        <p className='text-xs font-medium text-tertiary mb-1.5'>Destination</p>
        <div className='flex items-center gap-2'>
          <div className='flex-1 bg-bg-primary rounded-lg px-3 py-2 font-mono text-sm text-tertiary border border-border-secondary overflow-x-auto'>
            {webhookUrl}
          </div>
          <Button
            color='secondary'
            size='sm'
            iconLeading={webhookCopied ? Check : Copy01}
            onClick={onCopyWebhook}
            title='Copy destination URL'
          />
        </div>
      </div>

      {/* API Key */}
      <div>
        <p className='text-xs font-medium text-tertiary mb-1.5'>API Key</p>
        <p className='text-xs text-tertiary mb-2'>
          In Kondo's API Key field, set the header name to{' '}
          <code className='bg-bg-primary px-1 rounded'>X-Api-Key</code> and the
          value to the key below.
        </p>
        {newApiKey && (
          <>
            <div className='flex items-center gap-2'>
              <div className='flex-1 bg-bg-primary rounded-lg px-3 py-2 font-mono text-sm text-primary border border-border-secondary overflow-x-auto'>
                {newApiKey.key}
              </div>
              <Button
                color='secondary'
                size='sm'
                iconLeading={newKeyCopied ? Check : Copy01}
                onClick={onCopyNewKey}
                title='Copy API key'
              />
            </div>
            <p className='text-xs text-utility-warning-700 mt-2'>
              This key won't be shown again after you leave this page – save it
              or paste it into Kondo now.
            </p>
          </>
        )}
        {!newApiKey && isConnected && (
          <p className='text-xs text-tertiary'>
            The API key is shown only once, when you connect. If you no longer
            have it, use "Regenerate key" above to generate a new one.
          </p>
        )}
        {!newApiKey && !isConnected && (
          <p className='text-xs text-tertiary'>
            Your API key will appear here once you connect.
          </p>
        )}
      </div>
    </div>
  );
}
